import React, { useState } from "react";
import moment from "moment";
import TripDetailsModal from "./TripDetailsModal";
import BreakModal from "./BreakModal";

const ItineraryItinerary = ({ 
  schedule, 
  isPhone, 
  currentWaypoint, 
  vehicleLocation, 
  fleetSettings, 
  googleMapsLoaded, 
  routeDetails,
  onStartBreak,
  onRequestSplitShift,
  // Phase B — itinerary-mode flag trip (bottom button)
  canFlag = false,
  onFlagClick,
  // Demand Response block props
  blocks = [],
  currentBlockIndex = 0,
  demandResponseBlockTransitionMins = 10,
  // Outbound mode props - for handling pickups while having passengers on vehicle
  outboundModeActive = false,
  onFirstStepClick = null,
  // Demand response mode flag - for consolidating dropoffs
  demandResponseMode = false,
  onHubDropoffClick = null,
  // End step click handler
  onEndStepClick = null
}) => {
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [selectedBreak, setSelectedBreak] = useState(null);
  
  // Calculate minutes until next block pickup (for countdown display)
  const getMinutesUntilNextBlock = () => {
    const nextBlock = blocks[currentBlockIndex + 1];
    if (!nextBlock || !nextBlock.trips || nextBlock.trips.length === 0) return null;
    
    const nextPickupTime = nextBlock.trips[0]?.StartDateTime || nextBlock.trips[0]?.eta;
    if (!nextPickupTime) return null;
    
    const minutesUntil = Math.floor((new Date(nextPickupTime) - new Date()) / 60000);
    return minutesUntil > 0 ? minutesUntil : null;
  };
  
  const minutesUntilNextBlock = getMinutesUntilNextBlock();
  const showNextBlockCountdown = minutesUntilNextBlock !== null && minutesUntilNextBlock <= demandResponseBlockTransitionMins;
  
  if (!schedule || schedule.length === 0) {
    return (
      <div className="flex items-center justify-center p-8 text-gray-500">
        No upcoming stops
      </div>
    );
  }

  // Helper function to determine step type display name and color
  const getStepTypeInfo = (job) => {
    const stepType = (job.type || "").toLowerCase();

    if (stepType === "start" || job.beginStep) {
      return { name: "Start", color: "#22c55e" }; // Green
    } else if (stepType === "end" || job.endStep) {
      return { name: "End", color: "#ef4444" }; // Red
    } else if (stepType === "break" || job.isBreak) {
      return { name: "Break", color: "#0ea5e9" }; // Blue
    } else if (stepType === "hub") {
      return { name: "Hub", color: "#10b981" }; // Emerald green for hub/return to base
    } else if (stepType === "pickup") {
      return {
        name: "Pickup",
        color: job.wheelChair ? "rgb(66 186 240)" : "rgb(255 210 5)",
      };
    } else if (stepType === "delivery" || stepType === "dropoff") {
      return {
        name: "Dropoff",
        color: job.wheelChair ? "rgb(66 186 240)" : "rgb(255 210 5)",
      };
    }

    return { name: stepType, color: "lightgray" };
  };

  // Filter out completed, noshowed, and cancelled steps - they should not be shown
  const visibleSchedule = schedule.filter(job => {
    const stepStatus = (job.StepStatus || job.Status || '').toLowerCase();
    const isCompleted = job.isCompleted || stepStatus === 'complete' || stepStatus === 'completed';
    const isNoShowed = stepStatus === 'noshowed' || stepStatus === 'noshow';
    const isCancelled = stepStatus === 'cancelled' || stepStatus === 'canceled';
    return !isCompleted && !isNoShowed && !isCancelled;
  });

  // In demand response mode, consolidate consecutive dropoffs into a single "Hub Dropoff" row
  // This replaces the individual dropoff items and the "Home Location" separator
  const processScheduleForDemandResponse = () => {
    if (!demandResponseMode) {
      // Not in demand response mode - return schedule as-is without any hub separators
      return visibleSchedule;
    }
    
    // Demand response mode - consolidate dropoffs into "Hub Dropoff" rows
    // Note: schedule order is authoritative (server sends in Sequence order) — do NOT re-sort here
    const result = [];
    let currentDropoffGroup = [];
    let hubDropoffCount = 0;
    
    let hubSeparatorCount = 0;
    
    visibleSchedule.forEach((job, index) => {
      const currentType = (job.type || job.StepDescription || job.StepName || '').toLowerCase();
      const isDropoff = currentType === 'delivery' || currentType === 'dropoff';
      const isHubStep = currentType === 'hub';
      const isEndStep = job.endStep || currentType === 'end' || job.StepDescription === 'End' || job.StepName === 'End';
      
      if (isDropoff) {
        // Accumulate dropoffs
        currentDropoffGroup.push(job);
      } else if (isHubStep) {
        // ⚡ FEBRUARY 2026: Convert hub steps to green separator markers
        // Flush any accumulated dropoffs before the hub separator
        if (currentDropoffGroup.length > 0) {
          hubDropoffCount++;
          const passengerCount = currentDropoffGroup.reduce((total, dropoff) => {
            const paxCount = dropoff.trip?.reservation?.passengers || 
                            dropoff.trip?.passengerCount || 
                            dropoff.additionalPassengers ? (parseInt(dropoff.additionalPassengers) + 1) : 1;
            return total + paxCount;
          }, 0);
          
          result.push({
            _isHubDropoff: true,
            dropoffSteps: currentDropoffGroup,
            passengerCount: passengerCount || currentDropoffGroup.length,
            tripCount: currentDropoffGroup.length,
            key: `hub-dropoff-${hubDropoffCount}`,
            eta: currentDropoffGroup[0]?.eta || currentDropoffGroup[0]?.StartDateTime
          });
          currentDropoffGroup = [];
        }
        
        hubSeparatorCount++;
        result.push({
          _isHomeLocationSeparator: true,
          key: `hub-separator-${hubSeparatorCount}`,
          blockNumber: hubSeparatorCount,
          hubStep: job, // Preserve original hub step data for reference
        });
      } else {
        // Non-dropoff, non-hub step - flush accumulated dropoffs first
        if (currentDropoffGroup.length > 0) {
          hubDropoffCount++;
          // Count total passengers in the dropoff group
          const passengerCount = currentDropoffGroup.reduce((total, dropoff) => {
            const paxCount = dropoff.trip?.reservation?.passengers || 
                            dropoff.trip?.passengerCount || 
                            dropoff.additionalPassengers ? (parseInt(dropoff.additionalPassengers) + 1) : 1;
            return total + paxCount;
          }, 0);
          
          result.push({
            _isHubDropoff: true,
            dropoffSteps: currentDropoffGroup,
            passengerCount: passengerCount || currentDropoffGroup.length,
            tripCount: currentDropoffGroup.length,
            key: `hub-dropoff-${hubDropoffCount}`,
            // Use the earliest ETA from the group
            eta: currentDropoffGroup[0]?.eta || currentDropoffGroup[0]?.StartDateTime
          });
          currentDropoffGroup = [];
        }
        
        // Add the current non-dropoff step
        result.push(job);
      }
    });
    
    // Flush any remaining dropoffs at the end
    if (currentDropoffGroup.length > 0) {
      hubDropoffCount++;
      const passengerCount = currentDropoffGroup.reduce((total, dropoff) => {
        const paxCount = dropoff.trip?.reservation?.passengers || 
                        dropoff.trip?.passengerCount || 
                        dropoff.additionalPassengers ? (parseInt(dropoff.additionalPassengers) + 1) : 1;
        return total + paxCount;
      }, 0);
      
      result.push({
        _isHubDropoff: true,
        dropoffSteps: currentDropoffGroup,
        passengerCount: passengerCount || currentDropoffGroup.length,
        tripCount: currentDropoffGroup.length,
        key: `hub-dropoff-${hubDropoffCount}`,
        eta: currentDropoffGroup[0]?.eta || currentDropoffGroup[0]?.StartDateTime
      });
    }
    
    return result;
  };
  
  const scheduleWithSeparators = processScheduleForDemandResponse();

  // Home Location separator component
  const HomeLocationSeparator = ({ blockNum, hubStep }) => {
    const hubName = hubStep?.LandmarkName || hubStep?.StopName || 'Hub Location';
    const hubEta = hubStep?.eta || hubStep?.StartDateTime;
    const formattedEta = hubEta ? new Date(hubEta).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : null;
    
    return (
    <div className="w-full my-2">
      <div 
        className="flex items-center justify-center py-3 px-4 rounded-lg border-2 border-dashed border-green-400 bg-green-50"
        style={{ cursor: 'default' }}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
          <div className="text-center">
            <span className="font-semibold text-green-800 text-base">{hubName}</span>
            <span className="text-green-600 text-sm ml-2">(Hub)</span>
            {formattedEta && (
              <span className="text-green-600 text-sm ml-2">ETA: {formattedEta}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
  };
  
  // Hub Dropoff consolidated row component (for demand response mode)
  const HubDropoffRow = ({ tripCount, passengerCount, eta, onClick }) => {
    const formattedEta = eta ? new Date(eta).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : null;
    
    return (
      <div 
        className="w-full mb-1"
        style={{ cursor: onClick ? 'pointer' : 'default' }}
        onClick={onClick}
      >
        <div 
          className="flex items-center justify-between py-3 px-4 rounded-lg bg-gradient-to-r from-green-500 to-green-600 text-white shadow-md"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-lg">Drop off at Hub</span>
              <span className="text-white/90 text-sm">
                {tripCount} trip{tripCount !== 1 ? 's' : ''} • {passengerCount} passenger{passengerCount !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end">
            {formattedEta && (
              <>
                <span className="text-white/70 text-xs">ETA:</span>
                <span className="font-semibold">{formattedEta}</span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      id="itinerary"
      className="flex flex-col w-full"
      style={{ height: !isPhone ? "100%" : "auto" }}
    >
    
      
      {scheduleWithSeparators.map((item, index) => {
        // Render Home Location separator
        if (item._isHomeLocationSeparator) {
          return <HomeLocationSeparator key={item.key} blockNum={item.blockNumber} hubStep={item.hubStep} />;
        }
        
        // Render Hub Dropoff consolidated row (demand response mode)
        if (item._isHubDropoff) {
          return (
            <HubDropoffRow 
              key={item.key}
              tripCount={item.tripCount}
              passengerCount={item.passengerCount}
              eta={item.eta}
              onClick={onHubDropoffClick ? () => onHubDropoffClick(item) : null}
            />
          );
        }
        
        const job = item;
        const originalIndex = visibleSchedule.indexOf(job);
        const stepInfo = getStepTypeInfo(job);
        const isCompleted = job.isCompleted || false;
        const scheduledTime =
          job.type === "pickup"
            ? job.trip?.pickup?.date
            : job.trip?.dropoff?.date;
        const formattedScheduledTime = scheduledTime
          ? new Date(scheduledTime).toLocaleTimeString([], {
              hour: "numeric",
              minute: "2-digit",
            })
          : "N/A";
            const etaTime= new Date(job.eta);
  //add WaitingSecs to etaTime
  if (job.WaitingSecs) {
    etaTime.setSeconds(etaTime.getSeconds() );
  }
  
        const landmarkName = job.type === "pickup"
          ? job.trip?.pickup?.landmark
          : job.trip?.dropoff?.landmark; 
        
        // Check if break step is clickable (pending status, not completed, not already started)
        const isBreakClickable = job.isBreak && 
          !isCompleted && 
          !job.isArrived && 
          job.StepStatus !== 'arrived' && 
          job.StepStatus !== 'complete' &&
          job.StepStatus !== 'splitShiftApproved';
        
        // Get break status for styling
        const breakStatus = job.isBreak ? job.StepStatus || job.Status : null;
        const isSplitShiftRequested = breakStatus === 'splitShiftRequested';
        const isSplitShiftApproved = breakStatus === 'splitShiftApproved';
        const isSplitShiftRejected = breakStatus === 'splitShiftRejected';
        
        // Determine break background color based on status
        let breakBgColor = "#e0f2fe"; // Default light blue
        if (isSplitShiftRequested) breakBgColor = "#fef3c7"; // Amber for pending request
        if (isSplitShiftApproved) breakBgColor = "#d1fae5"; // Green for approved
        if (isSplitShiftRejected) breakBgColor = "#fee2e2"; // Red for rejected
        
        return (
          <div
            key={`${job.tripNo}-${job.type}-${index}`}
            className="w-full"
            style={{
              cursor: job.isBreak ? (isBreakClickable ? "pointer" : "default") : "pointer",
              opacity: isCompleted ? 0.5 : 1, // Dim completed steps
            }}
            onClick={() => {
              if (job.isBreak && isBreakClickable) {
                setSelectedBreak(job);
              } else if (!job.isBreak) {
                // Check if this is an end step
                const stepType = (job.type || job.StepDescription || job.StepType || '').toLowerCase();
                const isEndStep = job.endStep || stepType === 'end';
                
                if (isEndStep && onEndStepClick) {
                  // End step clicked - set it as current waypoint
                  onEndStepClick(job);
                } else {
                  // In outbound mode with passengers, clicking first pickup step loads it as current waypoint
                  const isFirstStep = originalIndex === 0;
                  const isPickup = (job.type || '').toLowerCase() === 'pickup';
                  if (outboundModeActive && isFirstStep && isPickup && onFirstStepClick) {
                    onFirstStepClick(job);
                  } else {
                    setSelectedTrip(job);
                  }
                }
              }
            }}
          >
            <div
              className="flex text-lg flex-col rounded overflow-hidden shadow-sm mb-1 relative p-1"
              style={{
                border: "1px solid lightgray",
                borderBottom: "0px",
                height: "auto",
                backgroundColor: isCompleted
                  ? "#e5e7eb" // Gray background for completed steps
                  : job.isBreak
                  ? breakBgColor
                  : "white",
              }}
            >
              {/* Checkmark badge for completed steps */}
              {isCompleted && (
                <div
                  className="absolute top-1 right-1 bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center z-10"
                  style={{ fontSize: "14px", fontWeight: "bold" }}
                >
                  ✓
                </div>
              )}
              {/* "Tap to Pickup" indicator for first pickup step in outbound mode */}
              {outboundModeActive && originalIndex === 0 && (job.type || '').toLowerCase() === 'pickup' && onFirstStepClick && !isCompleted && (
                <div
                  className="absolute top-1 right-1 bg-blue-600 text-white rounded-full px-2 py-1 flex items-center justify-center z-10 text-xs font-semibold"
                >
                  Tap to Pickup
                </div>
              )}

              <div className="flex w-full ">
                {/* Left color bar */}
                {(stepInfo.name === "Pickup" || stepInfo.name === "Start") && (
                  <div
                    className={`w-1`}
                    style={{ backgroundColor: stepInfo.color }}
                  />
                )}
                {(stepInfo.name === "Break" || stepInfo.name === "End" || stepInfo.name === "Hub") && (
                  <div
                    className={`w-1`}
                    style={{ backgroundColor: stepInfo.color }}
                  />
                )}

                <div className="flex p-1 w-full">
                  <p className="w-8/12 text-lg text-left">
                    <span className="block">
                      <span
                        style={{
                          fontSize: "16px",
                          fontWeight:
                            stepInfo.name === "Start" || stepInfo.name === "End" || stepInfo.name === "Hub"
                              ? "bold"
                              : "normal",
                          color:
                            stepInfo.name === "Start" || stepInfo.name === "End" || stepInfo.name === "Hub"
                              ? stepInfo.color
                              : job.isBreak && isSplitShiftRequested
                              ? "#b45309"
                              : job.isBreak && isSplitShiftApproved
                              ? "#059669"
                              : "inherit",
                        }}
                      >
                        {job.isBreak && isSplitShiftRequested 
                          ? "Split Shift Requested"
                          : job.isBreak && isSplitShiftApproved
                          ? "Split Shift Approved"
                          : job.isBreak && isSplitShiftRejected
                          ? "Break (Request Declined)"
                          : stepInfo.name === "Hub"
                          ? "Return to Hub"
                          : stepInfo.name}:
                      </span>{" "}
                      {stepInfo.name === "Hub" ? "Drop off passengers" : job.passengerName}
                    </span>
                    {job.isBreak ? (
                      <span className="text-base text-gray-600">
                        {job.startTime} - {job.endTime}
                        {isSplitShiftRequested && (
                          <span className="ml-2 text-amber-600 font-medium">Awaiting approval</span>
                        )}
                        {isSplitShiftApproved && (
                          <span className="ml-2 text-green-600 font-medium">Tap to clock out</span>
                        )}
                        {isBreakClickable && !isSplitShiftRequested && !isSplitShiftApproved && (
                          <span className="ml-2 text-blue-600 font-medium">Tap to take break</span>
                        )}
                      </span>
                    ) : stepInfo.name === "Hub" ? (
                      <span className="text-base text-gray-600">
                        {job.LandmarkName || "Hub Location"}
                      </span>
                    ) : (
                      landmarkName ? landmarkName : job.address
                    )}
                  </p>
                  <p className="w-1/12 text-lg text-right">
                  {job.wheelChair ? "🧑🏽‍🦽":"🚶‍➡️"}
                  </p>
                  <p className="w-3/12 text-lg text-right whitespace-nowrap">
                    <div>
                      <div className="flex justify-between">
                        <div className="w-12 text-left">Schd:</div>
                        <div>{formattedScheduledTime}</div>
                      </div>
                      <div className="flex justify-between">
                        <div className="w-12 text-left">ETA:</div>
                        <div>
                          {etaTime
                            ? moment(etaTime, "hh:mm:ss A").format("h:mm A")
                            : job.scheduledTime || "N/A"}
                        </div>
                      </div>
                    </div>
                    <div>
                      {job.etaDistance
                        ? `${job.etaDuration} mins - ${parseInt(
                            job.etaDistance
                          ).toFixed(1)} mi`
                        : ""}
                    </div>
                  </p>
                </div>

                {/* Right color bar */}
                {stepInfo.name === "Dropoff" && (
                  <div
                    className={`w-1`}
                    style={{ backgroundColor: stepInfo.color }}
                  />
                )}
              </div>
              
              {/* Comments section - full width with color bars */}
              {!job.isBreak && job.trip && (job.trip.pickup?.comments || job.trip.dropoff?.comments) && (
                <div className="flex w-full">
                  {/* Left color bar for pickup */}
                  {job.type === "pickup" && (
                    <div
                      className={`w-1`}
                      style={{ backgroundColor: stepInfo.color }}
                    />
                  )}
                  
                  <div className="px-2 pb-1 flex-1">
                    {job.type === "pickup" && job.trip.pickup?.comments && (
                      <div className="text-base text-gray-600">{job.trip.pickup.comments}</div>
                    )}
                    {(job.type === "delivery" || job.type === "dropoff") && job.trip.dropoff?.comments && (
                      <div className="text-base text-gray-600">{job.trip.dropoff.comments}</div>
                    )}
                  </div>
                  
                  {/* Right color bar for dropoff */}
                  {(job.type === "delivery" || job.type === "dropoff") && (
                    <div
                      className={`w-1`}
                      style={{ backgroundColor: stepInfo.color }}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Phase B — Flag Trip button (bottom of the itinerary listing) */}
      {canFlag && (
        <div className="w-full mt-2 px-1">
          <button
            onClick={onFlagClick}
            className="w-full py-3 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-semibold shadow"
          >
            Flag Trip
          </button>
        </div>
      )}

      {/* Trip Details Modal */}
      {selectedTrip && (
        <TripDetailsModal
          trip={selectedTrip}
          onClose={() => setSelectedTrip(null)}
        />
      )}

      {/* Break Modal */}
      {selectedBreak && (
        <BreakModal
          isOpen={!!selectedBreak}
          onClose={() => setSelectedBreak(null)}
          breakStep={selectedBreak}
          itinerary={schedule}
          currentWaypoint={currentWaypoint}
          vehicleLocation={vehicleLocation}
          fleetSettings={fleetSettings}
          googleMapsLoaded={googleMapsLoaded}
          routeDetails={routeDetails}
          onStartBreak={(breakStep) => {
            setSelectedBreak(null);
            if (onStartBreak) onStartBreak(breakStep);
          }}
          onRequestSplitShift={(breakStep) => {
            setSelectedBreak(null);
            if (onRequestSplitShift) onRequestSplitShift(breakStep);
          }}
        />
      )}
    </div>
  );
};

export default ItineraryItinerary;
